import React from 'react';
import { View, TouchableOpacity, Image, Linking, StyleSheet, ViewStyle, TextStyle } from 'react-native';
import { Text } from '@/components/common/text';
import { formatMessageTime } from '@/utils/common/formatting';
import { SVG_COMPONENTS as ADMIN_SVG_COMPONENTS } from '@/assets/admin';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';

const PaperclipIcon = ADMIN_SVG_COMPONENTS.PAPERCLIP;

// ============================================================================
// Theme Types
// ============================================================================

export interface MessageBubbleTheme {
  // Container styles
  sentContainerStyle?: ViewStyle;
  receivedContainerStyle?: ViewStyle;
  // Bubble styles
  sentBubbleColor: string;
  receivedBubbleColor: string;
  receivedBubbleBorderColor?: string;
  // Text styles
  sentTextColor: string;
  receivedTextColor: string;
  // Sender name styles
  senderNameColor: string;
  // Timestamp styles
  timestampColor: string;
  // Accent colors for replies
  accentColor: string;
}

// Default marketplace theme
export const MARKETPLACE_BUBBLE_THEME: MessageBubbleTheme = {
  sentBubbleColor: '#4672FE',
  receivedBubbleColor: '#1F1F1F',
  sentTextColor: '#FFFFFF',
  receivedTextColor: '#FFFFFF',
  senderNameColor: '#9CA3AF',
  timestampColor: 'rgba(156, 163, 175, 0.7)',
  accentColor: '#4672FE',
};

// Admin portal theme
export const ADMIN_BUBBLE_THEME: MessageBubbleTheme = {
  sentBubbleColor: '#4672FE',
  receivedBubbleColor: '#2A2A2A',
  sentTextColor: '#FFFFFF',
  receivedTextColor: '#FFFFFF',
  senderNameColor: '#9CA3AF',
  timestampColor: 'rgba(156, 163, 175, 0.7)',
  accentColor: '#4672FE',
};

// ============================================================================
// File Attachment Types
// ============================================================================

export interface MessageFile {
  id: string;
  url: string;
  name: string;
}

export interface ReplyToInfo {
  id: string;
  text: string;
  senderName: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

const isImageFile = (fileName: string): boolean => {
  const ext = fileName.toLowerCase().split('.').pop() || '';
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
};

// ============================================================================
// Sub-components
// ============================================================================

interface FileAttachmentsProps {
  files?: MessageFile[];
  isSent?: boolean;
  theme: MessageBubbleTheme;
}

const FileAttachments: React.FC<FileAttachmentsProps> = ({ files, isSent = false, theme }) => {
  const fileStyles = useThemedStyles(makeFileStyles);
  if (!files || files.length === 0) return null;

  const imageFiles = files.filter((f) => isImageFile(f.name));
  const documentFiles = files.filter((f) => !isImageFile(f.name));

  return (
    <View style={fileStyles.container}>
      {imageFiles.length > 0 && (
        <View style={fileStyles.imagesRow}>
          {imageFiles.map((file) => (
            <TouchableOpacity
              key={file.id}
              onPress={() => Linking.openURL(file.url)}
              style={fileStyles.imageTouchable}
            >
              <Image
                source={{ uri: file.url }}
                style={fileStyles.imageThumb}
                resizeMode="cover"
              />
            </TouchableOpacity>
          ))}
        </View>
      )}
      {documentFiles.map((file) => (
        <TouchableOpacity
          key={file.id}
          onPress={() => Linking.openURL(file.url)}
          style={fileStyles.documentRow}
        >
          <PaperclipIcon
            width={14}
            height={14}
            color={isSent ? 'rgba(255,255,255,0.7)' : theme.senderNameColor}
          />
          <Text
            style={[
              fileStyles.documentName,
              { color: isSent ? 'rgba(255,255,255,0.9)' : theme.receivedTextColor },
            ]}
            numberOfLines={1}
          >
            {file.name}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );
};

const makeFileStyles = ({ colors }: ThemeContextValue) => StyleSheet.create({
  container: {
    marginTop: 8,
  },
  imagesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 4,
  },
  imageTouchable: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  imageThumb: {
    width: 120,
    height: 120,
    borderRadius: 8,
    backgroundColor: colors.bg.base,
  },
  documentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    gap: 6,
  },
  documentName: {
    fontSize: 13,
    flex: 1,
  },
});

interface InlineReplyPreviewProps {
  replyTo: ReplyToInfo;
  isSent?: boolean;
  theme: MessageBubbleTheme;
}

const InlineReplyPreview: React.FC<InlineReplyPreviewProps> = ({ replyTo, isSent = false, theme }) => {
  const truncatedText = replyTo.text.length > 60 ? `${replyTo.text.substring(0, 60)}...` : replyTo.text;

  return (
    <View
      style={[
        replyStyles.container,
        isSent ? replyStyles.containerSent : { borderLeftColor: theme.accentColor, backgroundColor: `${theme.accentColor}15` },
      ]}
    >
      <Text
        style={[replyStyles.senderName, { color: isSent ? 'rgba(255, 255, 255, 0.9)' : theme.accentColor }]}
        numberOfLines={1}
      >
        {replyTo.senderName}
      </Text>
      <Text
        style={[replyStyles.messageText, { color: isSent ? 'rgba(255, 255, 255, 0.7)' : theme.senderNameColor }]}
        numberOfLines={2}
      >
        {truncatedText || '(Image)'}
      </Text>
    </View>
  );
};

const replyStyles = StyleSheet.create({
  container: {
    borderLeftWidth: 2,
    paddingLeft: 8,
    paddingVertical: 4,
    marginBottom: 6,
    borderRadius: 2,
  },
  containerSent: {
    borderLeftColor: 'rgba(255, 255, 255, 0.5)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  senderName: {
    fontSize: 11,
    fontWeight: '600',
    lineHeight: 14,
    marginBottom: 2,
  },
  messageText: {
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
  },
});

// ============================================================================
// Main Component
// ============================================================================

export interface BaseMessageBubbleProps {
  /** Message text content */
  text?: string;
  /** Timestamp for the message */
  timestamp: Date | string;
  /** Whether the message was sent by the current user */
  isSent: boolean;
  /** Sender's display name (for received messages) */
  senderName?: string;
  /** File attachments */
  files?: MessageFile[];
  /** Reply-to information */
  replyTo?: ReplyToInfo;
  /** Called when the menu button is pressed */
  onMenuPress?: () => void;
  /** Menu button icon component */
  menuIcon?: React.ReactNode;
  /** Theme configuration */
  theme?: MessageBubbleTheme;
  /** Optional left element (e.g., avatar) for received messages */
  leftElement?: React.ReactNode;
  /** Show sender name at top (default: true for received) */
  showSenderName?: boolean;
  /** Show sender name at bottom instead of top */
  senderNameAtBottom?: boolean;
}

/**
 * Base message bubble component that can be themed for different contexts.
 * Used as the foundation for marketplace, admin, and property chat message bubbles.
 */
export const BaseMessageBubble: React.FC<BaseMessageBubbleProps> = ({
  text,
  timestamp,
  isSent,
  senderName,
  files,
  replyTo,
  onMenuPress,
  menuIcon,
  theme = MARKETPLACE_BUBBLE_THEME,
  leftElement,
  showSenderName = !isSent,
  senderNameAtBottom = false,
}) => {
  const bubbleColor = isSent ? theme.sentBubbleColor : theme.receivedBubbleColor;
  const textColor = isSent ? theme.sentTextColor : theme.receivedTextColor;

  return (
    <View style={[styles.container, isSent ? styles.sentContainer : styles.receivedContainer]}>
      {!isSent && leftElement}
      <View
        style={[
          styles.bubble,
          {
            backgroundColor: bubbleColor,
            borderWidth: !isSent && theme.receivedBubbleBorderColor ? 1 : 0,
            borderColor: theme.receivedBubbleBorderColor || 'transparent',
          },
        ]}
      >
        {/* Header with sender name and menu */}
        {(showSenderName && senderName && !senderNameAtBottom) || onMenuPress ? (
          <View style={styles.header}>
            {showSenderName && senderName && !senderNameAtBottom && (
              <Text style={[styles.senderName, { color: isSent ? 'rgba(255,255,255,0.8)' : theme.senderNameColor }]}>
                {isSent ? 'You' : senderName}
              </Text>
            )}
            {onMenuPress && (
              <TouchableOpacity style={styles.menuButton} onPress={onMenuPress} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                {menuIcon}
              </TouchableOpacity>
            )}
          </View>
        ) : null}

        {/* Reply preview */}
        {replyTo && <InlineReplyPreview replyTo={replyTo} isSent={isSent} theme={theme} />}

        {/* Message text */}
        {text ? <Text scale="content" style={[styles.messageText, { color: textColor }]}>{text}</Text> : null}

        {/* File attachments */}
        <FileAttachments files={files} isSent={isSent} theme={theme} />

        {/* Footer with timestamp and optional sender name */}
        <View style={styles.footer}>
          <Text style={[styles.timestamp, { color: theme.timestampColor }]}>{formatMessageTime(timestamp)}</Text>
          {showSenderName && senderName && senderNameAtBottom && (
            <Text style={[styles.senderNameBottom, { color: theme.timestampColor }]}>
              {isSent ? 'You' : senderName}
            </Text>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 4,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
  },
  sentContainer: {
    justifyContent: 'flex-end',
  },
  receivedContainer: {
    justifyContent: 'flex-start',
  },
  bubble: {
    maxWidth: '80%',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  senderName: {
    fontSize: 12,
    fontWeight: '500',
  },
  menuButton: {
    padding: 4,
  },
  messageText: {
    fontSize: 16,
    lineHeight: 20,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginTop: 8,
  },
  timestamp: {
    fontSize: 12,
    opacity: 0.7,
  },
  senderNameBottom: {
    fontSize: 12,
    marginLeft: 16,
    opacity: 0.7,
  },
});

export default BaseMessageBubble;

